import { InteractiveMapLocation } from "@redotech/redo-common-model-location-common/interactive-map-location";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { LocationsMapModalBody } from "@redotech/redo-web/interactive-map/interactive-map";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback, useState } from "react";
import { LocationOutsideRadiusWarning } from "../../../submission/screens/shipment-method/location-outside-radius-warning";

export interface DropoffLocationsSearchParams {
  draftReturnId?: string;
  returnId?: string;
}

export const DropoffLocationsButton = memo(function DropoffLocationsButton({
  draftReturnId,
  returnId,
  customerCountry,
  autoOpen = false,
  modalTitle,
  modalSubtitle,
  text,
  searchDropoffLocations,
}: {
  draftReturnId?: string;
  returnId?: string;
  customerCountry?: string;
  autoOpen?: boolean;
  modalTitle?: string;
  modalSubtitle?: string;
  text?: string;
  searchDropoffLocations: (
    params: DropoffLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(autoOpen);
  const renderLocationExtra = useCallback(
    (location: InteractiveMapLocation) => (
      <LocationOutsideRadiusWarning location={location} />
    ),
    [],
  );
  const query = useQuery({
    queryKey: ["dropoffLocations", draftReturnId, returnId],
    queryFn: () => searchDropoffLocations({ draftReturnId, returnId }),
    enabled: isOpen,
    staleTime: Infinity,
  });

  return (
    <Flex>
      <RedoButton
        hierarchy="secondary"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
        text={text ?? t("See all locations")}
      />
      <RedoModal
        isOpen={isOpen}
        onModalCloseRequested={() => setIsOpen(false)}
        subtitle={modalSubtitle}
        title={modalTitle ?? t("Find drop-off locations")}
      >
        <LocationsMapModalBody
          customerCountry={customerCountry}
          error={!!query.error}
          errorText={t("Error loading drop-off locations")}
          locations={query.data ?? null}
          pending={query.isPending && query.fetchStatus !== "idle"}
          renderLocationExtra={renderLocationExtra}
        />
      </RedoModal>
    </Flex>
  );
});
